/**
 * Roles List Page
 *
 * Lists all roles in a table format with search, add, edit, and delete functionality
 * Follows reference pattern with deep query aggregations for policy counts
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
    Plus,
    Search,
    Trash2,
    Shield,
    Loader2,
    FileKey,
    Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useRoles, useDeleteRole } from '@/hooks/useAccessControl';
import type { RoleWithCounts } from '@/stores/access-control';

// Highlight matching text in search results
function HighlightText({ text, highlight }: { text: string; highlight: string }) {
    if (!highlight.trim()) {
        return <span>{text}</span>;
    }
    const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return (
        <span>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <mark key={i} className="bg-yellow-200 text-inherit">
                        {part}
                    </mark>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </span>
    );
}

export function RolesPage() {
    const navigate = useNavigate();
    const { data: roles, isLoading, error } = useRoles();
    const deleteRole = useDeleteRole();
    const [search, setSearch] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    // Filter roles by search
    const filteredRoles = roles?.filter(
        (role) =>
            role.name.toLowerCase().includes(search.toLowerCase()) ||
            role.description?.toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = async (id: string) => {
        try {
            await deleteRole.mutateAsync(id);
            setDeleteConfirm(null);
        } catch (err) {
            console.error('Failed to delete role:', err);
        }
    };

    const handleRowClick = (role: RoleWithCounts) => {
        navigate(`/admin/settings/roles/${role.id}`);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                Failed to load roles. Please try again.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-foreground">Roles</h2>
                    <p className="text-sm text-muted-foreground">
                        Manage user roles and their hierarchy
                    </p>
                </div>
                <Button asChild>
                    <Link to="/admin/settings/roles/new">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Role
                    </Link>
                </Button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                />
            </div>

            {/* Roles Table */}
            {filteredRoles?.length === 0 ? (
                <div className="rounded-lg border border-border bg-card p-8 text-center">
                    <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
                    <p className="mt-4 text-muted-foreground">
                        {search ? 'No roles match your search.' : 'No roles found.'}
                    </p>
                    {!search && (
                        <Link
                            to="/admin/settings/roles/new"
                            className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline"
                        >
                            <Plus className="h-4 w-4" />
                            Create your first role
                        </Link>
                    )}
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-border bg-card">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border bg-muted/30">
                                <th className="w-[42px] px-4 py-3 text-left"></th>
                                <th className="w-[200px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                                    Name
                                </th>
                                <th className="w-[100px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <Users className="h-3.5 w-3.5" />
                                        Users
                                    </div>
                                </th>
                                <th className="w-[100px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <FileKey className="h-3.5 w-3.5" />
                                        Policies
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                                    Description
                                </th>
                                <th className="w-[60px] px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRoles?.map((role) => (
                                <tr
                                    key={role.id}
                                    onClick={() => handleRowClick(role)}
                                    className="cursor-pointer border-b border-border last:border-b-0 hover:bg-accent/50 transition-colors"
                                >
                                    {/* Icon */}
                                    <td className="px-4 py-3">
                                        <div className="flex h-8 w-8 items-center justify-center rounded text-primary">
                                            <Shield className="h-5 w-5" />
                                        </div>
                                    </td>

                                    {/* Name */}
                                    <td className="px-4 py-3">
                                        <span className="font-medium text-foreground">
                                            <HighlightText text={role.name} highlight={search} />
                                        </span>
                                    </td>

                                    {/* User Count */}
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-muted-foreground">
                                            {role.userCount ?? 0}
                                        </span>
                                    </td>

                                    {/* Policy Count */}
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-muted-foreground">
                                            {role.policyCount ?? 0}
                                        </span>
                                    </td>

                                    {/* Description */}
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-muted-foreground line-clamp-1">
                                            {role.description ? (
                                                <HighlightText text={role.description} highlight={search} />
                                            ) : (
                                                <span className="italic opacity-50">
                                                    No description
                                                </span>
                                            )}
                                        </span>
                                    </td>

                                    {/* Actions */}
                                    <td className="px-4 py-3">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteConfirm(role.id);
                                            }}
                                            className="h-8 w-8 text-muted-foreground hover:bg-red-100 hover:text-red-600"
                                            title="Delete role"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Role</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this role? This action cannot be
                            undone. Users assigned to this role will lose their permissions.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
                            disabled={deleteRole.isPending}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleteRole.isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
